import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter } from 'rxjs';

import {
  CartOfferPriceMismatch,
  unexpectedMismatchBetweenCartAndOfferPricesSelector,
} from '@fcom/booking/store/selectors/booking-cart-state.selector';
import {
  boundsCurrencySelector,
  selectedInboundFareFamilySelector,
  selectedOutboundFareFamilySelector,
} from '@fcom/common/store/selectors/bounds.selector';
import { BookingAndAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { PreciseNumber } from '@fcom/core/utils/precise-number';
import { cartDataSelector } from '@fcom/booking/store/selectors/cart.selector';
import { FareFamily } from '@fcom/dapi/api/models/fare-family';
import { FinnairAmount } from '@fcom/dapi/api/models/finnair-amount';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairPassengerCode } from '@fcom/dapi/api/models/finnair-passenger-code';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { BoundType, LocationInfoMap } from '@fcom/dapi/interfaces/itinerary.interface';
import { ButtonMode, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { ModalButtons } from '@fcom/ui-components/components/modal/interfaces';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';

import { hasWideBodyFlight, toAmount } from '../../utils/common-booking.utils';
import { selectionDeltaSelector } from '../../store/selectors/cart-selection-diff.selector';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { getPassengerAndInfantIds, priceForPassengerTypeCode } from '../../utils/shopping-cart.utils';
import { applyDelta, filterDeltaByPax } from '../../utils/cart-selection-diff.utils';

// Sentinel passenger key for the "no passenger tab selected" (all-passengers, merged) view.
export const ALL_PASSENGERS = '*';

interface FareFamilyModalData {
  isOpen: boolean;
  fareFamily?: FareFamily;
  hasWideBodyAircraft?: boolean;
}

interface ItineraryModalData {
  isOpen: boolean;
  locations?: LocationInfoMap;
  bound?: FinnairBoundItem;
  boundType?: BoundType;
}

@Component({
  selector: 'fin-shopping-cart',
  standalone: false,
  templateUrl: './shopping-cart.component.html',
  styleUrl: './shopping-cart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartComponent implements OnInit {
  protected readonly ModalButtons = ModalButtons;
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonMode = ButtonMode;

  private store$ = inject<Store<BookingAndAppState>>(Store);
  protected shoppingCartService = inject(ShoppingCartService);
  monitoringLogger = inject(MonitoringLogger);
  private destroyRef = inject(DestroyRef);

  isInsideModal = input(false);
  showModalCtaButton = input(false);
  modalCtaButtonText = input<string>();
  modalCtaButtonClick = output();

  cart = this.store$.selectSignal(cartDataSelector);
  selectedPassengerId = signal<string | null>(null);
  private outboundFareFamily = this.store$.selectSignal(selectedOutboundFareFamilySelector);
  private inboundFareFamily = this.store$.selectSignal(selectedInboundFareFamilySelector);
  private currency = this.store$.selectSignal(boundsCurrencySelector);
  private selectionDelta = this.store$.selectSignal(selectionDeltaSelector);

  // The whole-journey total: the committed cart total (backend-authoritative, incl. flight fares, taxes and
  // combo discounts) with the local selection delta applied on top. Collapses to the committed total when
  // the selection matches the cart.
  cartTotal: Signal<FinnairAmount | undefined> = computed(() => {
    const journeyTotal = this.cart()?.prices?.unpaid?.total?.total?.totalAmount;
    if (!journeyTotal) {
      return undefined;
    }
    return {
      ...journeyTotal,
      amount: applyDelta(PreciseNumber.fromString(journeyTotal.amount), this.selectionDelta()).toString(),
    };
  });

  cartTotalPricePerPassenger: Signal<Amount | undefined> = computed(() => {
    const cart = this.cart();
    const passengerId = this.selectedPassengerId();
    const unpaidTotal = cart?.prices?.unpaid?.total;
    const delta = this.selectionDelta();

    // Per-passenger view: sum the committed per-pax totals of the passenger and any accompanying infant,
    // then apply only their share of the delta.
    if (passengerId && unpaidTotal?.totalPerPax?.[passengerId]) {
      const paxIds = getPassengerAndInfantIds(cart, passengerId);
      const base = paxIds.reduce(
        (sum, id) => sum.add(PreciseNumber.fromString(unpaidTotal.totalPerPax[id]?.totalAmount?.amount ?? '0')),
        PreciseNumber.ZERO
      );
      return {
        ...unpaidTotal.totalPerPax[passengerId].totalAmount,
        amount: applyDelta(base, filterDeltaByPax(delta, paxIds)).toString(),
      };
    }

    // Whole-journey view (no passenger tab, or the selected passenger isn't in the cart yet mid-refetch).
    const journeyTotal = unpaidTotal?.total?.totalAmount;
    if (!journeyTotal) {
      return undefined;
    }
    return {
      ...journeyTotal,
      amount: applyDelta(PreciseNumber.fromString(journeyTotal.amount), delta).toString(),
    };
  });

  private selectedCartPassenger = computed(() => {
    const selectedPassengerId = this.selectedPassengerId();
    return selectedPassengerId ? this.cart()?.passengers?.find(({ id }) => id === selectedPassengerId) : undefined;
  });

  protected infantTravelingWithSelectedPassenger = computed(() => {
    const selectedPassengerId = this.selectedPassengerId();
    return selectedPassengerId
      ? this.cart()?.passengers?.find(({ accompanyingTravelerId }) => accompanyingTravelerId === selectedPassengerId)
      : undefined;
  });

  // The passenger count shown in the header: in the per-passenger view it's the passenger plus any
  // accompanying infant; in the whole-journey view it's the whole cart's passenger count.
  protected passengerCount = computed(() => {
    const passengerId = this.selectedPassengerId();
    if (!passengerId) {
      return this.cart()?.passengers?.length;
    }
    return this.infantTravelingWithSelectedPassenger() ? 2 : 1;
  });

  protected showCtaButton = computed(
    () => this.isInsideModal() && this.showModalCtaButton() && this.shoppingCartService.isAllowedToShowCtaInModal()
  );

  // Unify onto a single bounds source once multi-city pricing support exists.
  private boundsForPricing = computed(() => {
    const outbound = this.outboundFareFamily();
    if (!outbound) {
      return [];
    }
    const inbound = this.inboundFareFamily();
    return inbound ? [outbound, inbound] : [outbound];
  });

  offerBoundsPrices: Signal<Array<Amount | undefined>> = computed(() => {
    const currencyCode = this.currency();
    const bounds = this.boundsForPricing();

    if (!this.selectedPassengerId()) {
      return bounds.map((bound) => toAmount({ amount: bound.price, currencyCode }));
    }

    const cartPassenger = this.selectedCartPassenger();
    if (!cartPassenger) {
      return [];
    }
    const cartInfant = this.infantTravelingWithSelectedPassenger();
    return bounds.map((bound) => {
      const paxPrice = priceForPassengerTypeCode(bound, cartPassenger.passengerTypeCode)?.price;
      if (paxPrice == null) {
        return undefined;
      }
      const infantPrice = cartInfant ? (priceForPassengerTypeCode(bound, FinnairPassengerCode.INF)?.price ?? 0) : 0;
      return toAmount({ amount: paxPrice + infantPrice, currencyCode });
    });
  });
  showPassengerTabs = signal(false);

  fareFamilyModalData: FareFamilyModalData = {
    isOpen: false,
  };

  itineraryModalData: ItineraryModalData = {
    isOpen: false,
  };

  ngOnInit(): void {
    this.store$
      .select(unexpectedMismatchBetweenCartAndOfferPricesSelector)
      .pipe(
        filter(Boolean),
        // Skips re-logging the same still-unresolved mismatch on every unrelated cart update.
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((mismatch) => this.reportSentryErrorForPricesMismatch(mismatch));
  }

  closeShoppingCart(): void {
    // Close first so the modal's leave animation starts immediately; only then reset the passenger
    // selection, so resetting it doesn't force the whole cart to re-render on the closing frame.
    this.shoppingCartService.setVisible(false);
    this.selectedPassengerId.set(null);
  }

  openFareFamilyDetails(fareFamily: FareFamily, bound?: FinnairBoundItem): void {
    this.fareFamilyModalData.isOpen = true;
    this.fareFamilyModalData.fareFamily = fareFamily;
    this.fareFamilyModalData.hasWideBodyAircraft = hasWideBodyFlight(bound?.itinerary);
  }

  openItineraryDetails(
    bound: FinnairBoundItem,
    bounds: FinnairBoundItem[],
    index: number,
    locations: LocationInfoMap
  ): void {
    this.itineraryModalData.isOpen = true;
    this.itineraryModalData.bound = bound;
    this.itineraryModalData.boundType =
      bounds.length > 2 ? BoundType.multi : index === 0 ? BoundType.outbound : BoundType.inbound;
    this.itineraryModalData.locations = locations;
  }

  selectPassenger(passengerId: string | null): void {
    this.selectedPassengerId.set(passengerId);
  }

  handleModalCtaButtonClick(): void {
    this.modalCtaButtonClick.emit();
  }

  // The price shown per bound comes from the offer; the amount the customer is charged comes from the cart.
  // They should agree, so we report a Sentry error when they diverge. This only monitors; it never affects
  // what is displayed.
  private reportSentryErrorForPricesMismatch(mismatch: CartOfferPriceMismatch): void {
    this.monitoringLogger.error('Cart flight total does not match offer total', {
      cartId: mismatch.cartId,
      passengerId: mismatch.passengerId ?? ALL_PASSENGERS,
      cartAmount: mismatch.cartAmount,
      offerAmount: mismatch.offerAmount,
      offerOutboundPrice: mismatch.offerOutboundPrice,
      offerOutboundId: mismatch.offerOutboundId,
      offerInboundPrice: mismatch.offerInboundPrice,
      offerInboundId: mismatch.offerInboundId,
    });
  }
}
